import React, { useEffect, useState,useContext } from "react";
import { Button, Row, Col, Radio, Tag } from "antd";
import { SaveOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  Paper,
  TextField,
  Box,
  Typography,
  Divider,
  Button as MUIButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { URL as BASE_URL } from "../config";

import { LoaderContext } from "../Components/dashboard";


const themeColor = "#ff002b";

export default function AddBannerForm() {
  const { setLoading } = useContext(LoaderContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const [bannerName, setBannerName] = useState("");
  const [saving, setSaving] = useState(false);

  const [isActive, setIsActive] = useState(1);
  const [photoFile, setPhotoFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [removeImage, setRemoveImage] = useState(0);

  const [mode, setMode] = useState(id ? "edit" : "create");

useEffect(() => {
  let isMounted = true;
  const startTime = Date.now();

  setLoading(true); 

  const init = async () => {
    if (id) {
      await fetchDetails(); 
    }

    const elapsed = Date.now() - startTime;
    const remaining = 1500 - elapsed;

    setTimeout(() => {
      if (isMounted) {
        setLoading(false); 
      }
    }, remaining > 0 ? remaining : 0);
  };

  init();

  return () => {
    isMounted = false;
  };
}, [id]);

  useEffect(() => {
    setMode(id ? "edit" : "create");
    if (id) fetchDetails();
  }, [id]);


  const fetchDetails = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/addbanner/${id}`);
      const data = res.data.data;

      setBannerName(data.bannerName || "");
      setIsActive(data.isActive ?? 1);

      if (data.bannerImg) {
        const previewUrl = data.bannerImg.startsWith("http")
          ? data.bannerImg
          : `${BASE_URL}${data.bannerImg}`;
        setFilePreview(previewUrl);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load banner details");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setFilePreview(URL.createObjectURL(file));
    }
  };
const removeFile = () => {
  setPhotoFile(null);
  setFilePreview("");
  setRemoveImage(1); 
};


  const clearForm = () => {
    setBannerName("");
    setIsActive(1);
    setPhotoFile(null);
    setFilePreview("");
  };


  const handleSave = async () => {
    if (saving) return;
    if (!bannerName.trim()) {
      toast.error("Banner name is required");
      return;
    }

    try {
       setSaving(true);
      const formData = new FormData();
      formData.append("bannerName", bannerName);
      formData.append("isActive", isActive);
      if (photoFile) formData.append("bannerImg", photoFile);
      if (removeImage === 1) {
  formData.append("removeImage", "1");
}

      if (mode === "edit") {
        await axios.patch(`${BASE_URL}/addbanner/${id}`, formData);
        toast.success("Banner updated successfully");
        clearForm();
        setMode("create");
        window.history.replaceState({}, "", "/dashboard/addbanner");
      } else {
        await axios.post(`${BASE_URL}/addbanner`, formData);
        toast.success("Banner created successfully");
        clearForm();
      }
    } catch (err) {
      console.error(err);
      toast.error("Save failed");
    }
     finally {
    setSaving(false); 
  }
  };

  return (
    <Box sx={{ p: { xs: 0.1, md: 1 } }}>
      <Paper elevation={3} sx={{ p: { xs: 1, md: 3 }, borderRadius: 3 }}>
      
        <Box display="flex" justifyContent="center" mt={1.5} mb={1.5}>
          <Box
            sx={{
              background: `linear-gradient(90deg, ${themeColor}, #ff6a6a)`,
              px: { xs: 2.5, md: 4 },
              py: { xs: 0.8, md: 1.1 },
              borderRadius: "8px 22px 8px 22px",
              boxShadow: "0 6px 16px rgba(255,92,110,0.35)",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "14px", md: "18px" },
                fontWeight: 900,
                color: "#fff",
                letterSpacing: "0.7px",
                textTransform: "uppercase",
              }}
            >
              Add Banner
            </Typography>
          </Box>
        </Box>

        <Divider
          sx={{
            mb: { xs: 2, md: 3 },
            background: `linear-gradient(90deg, transparent, ${themeColor}, transparent)`,
            height: "2px",
            borderRadius: 5,
          }}
        />

      
        <Row gutter={[16, 16]} align="middle">
        
          <Col xs={24} sm={12} md={8}>
            <Typography
              fontWeight={600}
              mb={0.5}
              sx={{ color: themeColor, fontSize: { xs: "13px", md: "14px" } }}
            >
              Banner Name
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Enter banner name"
              value={bannerName}
              onChange={(e) => setBannerName(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  minHeight: 40,
                  "& fieldset": { borderColor: "#ccc" },
                  "&:hover fieldset": { borderColor: "#aaa" },
                  "&.Mui-focused fieldset": { borderColor: "#aaa" },
                },
                "& .MuiInputBase-input": {
                  fontSize: "13px",
                  padding: "8.5px 12px",
                },
              }}
            />
          </Col>

        
          <Col xs={24} sm={12} md={8}>
            <Typography
              fontWeight={600}
              mb={1}
              sx={{ color: themeColor, fontSize: { xs: "13px", md: "14px" } }}
            >
              Status
            </Typography>
            <Radio.Group
              value={isActive}
              onChange={(e) => setIsActive(e.target.value)}
            >
              <Radio value={1}>
                <Tag color="green">Active</Tag>
              </Radio>
              <Radio value={0}>
                <Tag color="red">Deactive</Tag>
              </Radio>
            </Radio.Group>
          </Col>

        
          <Col xs={24} sm={12} md={8}>
            <Typography
              fontWeight={600}
              mb={1}
              sx={{ color: themeColor, fontSize: { xs: "13px", md: "14px" } }}
            >
              Upload Banner Image
            </Typography>

            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <input
                id="banner-upload"
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
              <label htmlFor="banner-upload">
                <MUIButton
                  variant="outlined"
                  component="span"
                 sx={{ fontWeight: 600, fontSize: { xs: "12px", md: "14px" }, borderColor: "#aaa", color: "#8b8989", "&:hover": { borderColor: themeColor, background: "rgba(255,92,110,0.08)" } }}
                >
                  Upload Image
                </MUIButton>
              </label>

              {filePreview && (
                <Box position="relative">
                  <img
                    src={filePreview}
                    alt="preview"
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: 6,
                      objectFit: "cover",
                      border: `1px solid ${themeColor}`,
                    }}
                  />
                  <CloseIcon
                    onClick={removeFile}
                    sx={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      cursor: "pointer",
                      background: "#fff",
                      borderRadius: "50%",
                      fontSize: 16,
                      color: themeColor,
                    }}
                  />
                </Box>
              )}
            </Box>
          </Col>
        </Row>

       
        <Box
          display="flex"
          justifyContent="end"
          mt={4}
          gap={1.5}
          flexWrap={{ xs: "wrap", md: "nowrap" }}
        >
          <Button
            icon={<SaveOutlined />}
            type="primary"
            onClick={handleSave}
             loading={saving}          
  disabled={saving}
            style={{
              background: themeColor,
              borderRadius: 25,
              padding: "4px 20px",
              fontSize: 13,
            }}
          >
            {mode === "edit" ? "Update" : "Save"}
          </Button>

          <Button
            icon={<InfoCircleOutlined />}
            onClick={() => navigate("/dashboard/addbannerlist")}
            style={{
              borderRadius: 25,
              padding: "4px 20px",
              fontSize: 13
              ,color:"red",borderColor:"red"
            }}
            onMouseEnter={(e) => {
    e.currentTarget.style.backgroundColor = "#ffe6e9";
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.backgroundColor = "transparent";
  }}
          >
            Details
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
